import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { CurrencyProvider } from "./context/CurrencyContext";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import VisitTracker from "./components/VisitTracker";
import ProtectedRoute from "./components/ProtectedRoute";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import PresetBoxes from "./pages/PresetBoxes";
import BuildABox from "./pages/BuildABox";
import PartyBox from "./pages/PartyBox";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Login from "./pages/Login";
import Scan from "./pages/Scan";
import TrackOrder from "./pages/TrackOrder";
import CustomerAccount from "./pages/CustomerAccount";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import AdminLanding from "./pages/admin/Landing";
import AdminProducts from "./pages/admin/Products";
import AdminBoxes from "./pages/admin/Boxes";
import AdminFlavours from "./pages/admin/Flavours";
import AdminOrders from "./pages/admin/Orders";
import AdminInventory from "./pages/admin/Inventory";
import AdminBlog from "./pages/admin/BlogAdmin";
import AdminUsers from "./pages/admin/Users";
import AdminScanner from "./pages/admin/Scanner";
import AdminMyAccess from "./pages/admin/MyAccess";
import AdminReviews from "./pages/admin/Reviews";
import AdminCoupons from "./pages/admin/Coupons";
import AdminActivity from "./pages/admin/ActivityLog";
import AdminStockHistory from "./pages/admin/StockHistory";
import AdminNewsletter from "./pages/admin/Newsletter";
import AdminManifest from "./pages/admin/Manifest";
import AdminManifestLabels from "./pages/admin/ManifestLabels";
import AdminMaintenance from "./pages/admin/Maintenance";
import AdminSettings from "./pages/admin/Settings";
import AdminSecurity from "./pages/admin/Security";
import { Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";

function AdminIndex() {
  const { hasPermission } = useAuth();
  if (hasPermission("analytics.view")) return <Dashboard />;
  if (hasPermission("orders.manage") || hasPermission("orders.scan")) return <Navigate to="/admin/orders" replace />;
  return <Navigate to="/admin/my-access" replace />;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main className="min-h-[70vh]">{children}</main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CurrencyProvider>
          <CartProvider>
          <BrowserRouter>
            <VisitTracker />
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/shop" element={<PublicLayout><Shop /></PublicLayout>} />
              <Route path="/product/:id" element={<PublicLayout><ProductDetail /></PublicLayout>} />
              <Route path="/boxes" element={<PublicLayout><PresetBoxes /></PublicLayout>} />
              <Route path="/build" element={<PublicLayout><BuildABox /></PublicLayout>} />
              <Route path="/party" element={<PublicLayout><PartyBox /></PublicLayout>} />
              <Route path="/cart" element={<PublicLayout><Cart /></PublicLayout>} />
              <Route path="/checkout" element={<PublicLayout><Checkout /></PublicLayout>} />
              <Route path="/order/:id" element={<PublicLayout><OrderSuccess /></PublicLayout>} />
              <Route path="/track" element={<PublicLayout><TrackOrder /></PublicLayout>} />
              <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
              <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
              <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
              <Route path="/account" element={<ProtectedRoute><PublicLayout><CustomerAccount /></PublicLayout></ProtectedRoute>} />
              <Route path="/scan/:id" element={<PublicLayout><Scan /></PublicLayout>} />

              <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminIndex />} />
                <Route path="landing" element={<AdminLanding />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="boxes" element={<AdminBoxes />} />
                <Route path="flavours" element={<AdminFlavours />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="coupons" element={<AdminCoupons />} />
                <Route path="manifest" element={<AdminManifest />} />
                <Route path="manifest/labels" element={<AdminManifestLabels />} />
                <Route path="inventory" element={<AdminInventory />} />
                <Route path="stock-history" element={<AdminStockHistory />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="newsletter" element={<AdminNewsletter />} />
                <Route path="activity" element={<AdminActivity />} />
                <Route path="scanner" element={<AdminScanner />} />
                <Route path="maintenance" element={<AdminMaintenance />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="security" element={<AdminSecurity />} />
                <Route path="my-access" element={<AdminMyAccess />} />
              </Route>
            </Routes>
          </BrowserRouter>
          </CartProvider>
        </CurrencyProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
